// src/pages/public/PublicProfilePage.jsx
import { useState, useEffect, useMemo, useRef, useLayoutEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  User, MapPin, Building,  Users, Eye, TrendingUp, TrendingDown,
  Shield, Target, Activity, BarChart3,  MessageCircle, 
  Crown, Medal, ExternalLink, Share2, UserPlus, UserMinus,  Briefcase,
  Sparkles, ArrowRight, Loader2
} from 'lucide-react';


import ConnectionsTab from '../../components/profile/ConnectionTab';
import PublicAvatarDisplay from './utils/utils/PublicAvatarDisplay';
import UserPosts from './utils/utils/UserPostsTab';
import CredibilityChip from '../../components/Credibility/CredibilityChip';
import { InvestmentDisclaimerNotice, AIProcessingNotice } from '../../components/Compliance/ComplianceNotices';

import { fetchPublicProfile } from '../../redux/user/publicProfile/userPublicProfileThunks';
import { followUserThunk, unfollowUserThunk } from '../../redux/user/following/userFollowActionThunks';

/* ----------------------------- small helpers ------------------------------ */
const Spinner = () => (
  <div className="flex items-center justify-center py-32 bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent"></div>
  </div>
);


const normalizeParam = (s) =>
  decodeURIComponent(s || '')
    .replace(/^@/, '')
    .replace(/[:\s]+$/g, '');

const profileMatchesParam = (profile, param) => {
  if (!profile || !param) return false;
  const p = String(param).toLowerCase();
  return (
    String(profile?.id) === p ||
    String(profile?.username || '').toLowerCase() === p ||
    String(profile?.slug || '').toLowerCase() === p
  );
};


const getSentimentColor = (sentiment) =>
  sentiment === 'bull' ? 'text-green-800 dark:text-green-400 bg-green-100 dark:bg-green-500/10'
  : sentiment === 'bear' ? 'text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-500/10'
  : 'text-gray-400 bg-gray-500/10';

const getSentimentIcon = (sentiment) =>
  sentiment === 'bull' ? TrendingUp
  : sentiment === 'bear' ? TrendingDown
  : Activity;

const formatDate = (dateString) =>
  dateString
    ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '';


/* --------------------------------- page ----------------------------------- */

const PublicProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { slug } = useParams();

  // Route key (id or slug)
  const routeParam = normalizeParam(slug);
  
  // Redux selectors - must all be called unconditionally
  const profileState = useSelector((s) => s.profilepublic);
  const authState = useSelector((s) => s.auth);
  const { byTargetId: followStatusByUserId, loading: followLoading } = useSelector((s) => s.userFollow);
  const currentUser = useSelector((s) => s.auth?.user || s.user?.currentUser);

  // Destructure profile state
  const {
    loading,
    user: storedProfile,
    error,
    lastFetchedAt,
  } = profileState || {};

  // All state hooks
  const [activeTab, setActiveTab] = useState('overview');
  const [justFollowed, setJustFollowed] = useState(false);

  // Same scroll-position-preservation mechanism as MyAccount/profile.jsx --
  // AppLayout.jsx's <main overflow-y-auto> is this app's one real scroll
  // container (the window/body itself never scrolls), so window.scrollY
  // doesn't apply here. Tab content heights vary widely (a short "Overview"
  // vs. a tall "Connections" list) and some tabs' data loads in async after
  // the initial render, growing the container and pushing scrollTop around
  // even after a single one-time restore -- re-assert the saved position on
  // every frame for a short window after switching so late-arriving content
  // can't drag it back to the top on the user.
  const scrollPositionRef = useRef(0);
  const getScrollContainer = () => document.querySelector('main');
  const handleTabChange = useCallback((tabId) => {
    scrollPositionRef.current = getScrollContainer()?.scrollTop ?? 0;
    setActiveTab(tabId);
  }, []);
  useLayoutEffect(() => {
    const container = getScrollContainer();
    if (!container) return;
    container.scrollTop = scrollPositionRef.current;

    let cancelled = false;
    const stopPinning = () => { cancelled = true; };
    container.addEventListener('wheel', stopPinning, { passive: true, once: true });
    container.addEventListener('touchstart', stopPinning, { passive: true, once: true });

    let frame;
    const start = performance.now();
    const pin = (now) => {
      if (cancelled) return;
      container.scrollTop = scrollPositionRef.current;
      if (now - start < 800) frame = requestAnimationFrame(pin);
    };
    frame = requestAnimationFrame(pin);

    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('wheel', stopPinning);
      container.removeEventListener('touchstart', stopPinning);
    };
  }, [activeTab]);

  // All useMemo hooks
  const displayProfile = useMemo(() => {
    if (!storedProfile) return null;
    return profileMatchesParam(storedProfile, routeParam) ? storedProfile : null;
  }, [storedProfile, routeParam]);

  const avgPerformance = useMemo(() => {
    if (!Array.isArray(displayProfile?.stats?.top_tickers)) return null;
    const preds = displayProfile.stats.top_tickers;
    const totalPreds = preds.reduce((sum, t) => sum + (t.total_predictions || 0), 0);
    const correctPreds = preds.reduce((sum, t) => sum + (t.correct_predictions || 0), 0);

    if (totalPreds === 0) return null;
    return ((correctPreds / totalPreds) * 100).toFixed(1);
  }, [displayProfile?.stats?.top_tickers]);

  // Derived values
  const isOwnProfile = !!(currentUser?.id && displayProfile?.id && currentUser.id === displayProfile?.id);
  const serverSaysFollowing = typeof displayProfile?.viewer_is_following === 'boolean' ? displayProfile?.viewer_is_following : undefined;
  const isFollowing = serverSaysFollowing ?? (displayProfile?.id ? !!followStatusByUserId[displayProfile?.id] : false);
  const topSector = displayProfile?.credibility?.top_sectors?.[0]?.name || "N/A";
  const profile = displayProfile; // for readability below

  /* ------------------------- fetch on route change ------------------------- */
  useEffect(() => {
    if (!routeParam) return;
    
    setActiveTab('overview');
    
    const sameProfile = profileMatchesParam(storedProfile, routeParam);
    const fresh = sameProfile && lastFetchedAt && Date.now() - lastFetchedAt < 5 * 60 * 1000;
    
    if (!sameProfile || !fresh) {
      dispatch(fetchPublicProfile({ idOrSlug: routeParam }));
    }
  }, [routeParam, dispatch]); // Minimal dependencies to avoid refetch loops

  /* --------------------------- follow / unfollow --------------------------- */
  const handleFollowToggle = async () => {
    const targetId = displayProfile?.id;
    if (!targetId || isOwnProfile || followLoading) return;

    try {
      if (isFollowing) {
        await dispatch(unfollowUserThunk({ userId: targetId })).unwrap();
      } else {
        await dispatch(followUserThunk({ userId: targetId })).unwrap();
        setJustFollowed(true);
      }
    } catch (err) {
      if (String(err).toLowerCase().includes('not currently followed')) {
        console.warn('Server says not following; syncing UI to not-following.');
      } else {
        console.warn('Follow action failed:', err);
      }
    }
  };

  /* ------------------------------- NOW check conditions -------------------------------- */
  
  // Check Redux initialization
  if (!profileState || !authState) {
    return <Spinner />;
  }

  // Loader: only when actually fetching
  if (loading && !displayProfile) {
    return <Spinner />;
  }

  // Error or no profile
  if (error || !displayProfile) {
    return (
      <div className="flex items-center justify-center py-32 bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
        <div className="text-center">
          <div className="text-6xl mb-4">😕</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t('publicProfile.profileNotFoundTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{error || t('publicProfile.noProfileData')}</p>
          <button
            onClick={() => dispatch(fetchPublicProfile({ idOrSlug: routeParam }))}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {t('publicProfile.tryAgain')}
          </button>
        </div>
      </div>
    );
  }


  /* -------------------------------- render -------------------------------- */

  return (
            <div className="max-w-7xl mx-auto p-8 pb-24 lg:pb-8 bg-gray-50 dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-900 dark:to-blue-900">
              {/* Header */}
              <div className="relative">
                <div className="h-64 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 relative overflow-hidden">
                  <div className="absolute inset-0 bg-black/30"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full blur-xl animate-pulse"></div>
                  <div className="absolute bottom-10 right-20 w-32 h-32 bg-purple-500/20 rounded-full blur-2xl animate-pulse delay-1000"></div>
                </div>

                <div className="max-w-6xl mx-auto px-6 relative">
                  <div className="flex flex-col lg:flex-row lg:items-end lg:space-x-8 -mt-32">
                    {/* Avatar */}
                    <div className="relative flex-shrink-0 self-center lg:self-start">
                      <PublicAvatarDisplay userId={profile?.id} size="w-48 h-48" />
                      <div className="absolute -top-3 -right-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 px-4 py-2 rounded-full text-sm font-bold shadow-lg border-2 border-white/20">
                        {t('publicProfile.levelBadge', { level: profile?.gamification?.level || 1 })}
                      </div>
                    </div>


                    {/* Profile Info */}
                    <div className="pt-8 lg:pt-0 flex-1 min-w-0">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                        <div className="flex-1 min-w-0">
                          <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-3 dark:bg-gradient-to-r dark:from-white dark:to-gray-300 dark:bg-clip-text dark:text-transparent">
                            {profile?.full_name || profile?.username || t('publicProfile.unknownUser')}
                          </h1>

                          {profile?.professional_title && (
                            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">{profile?.professional_title}</p>
                          )}

                          <div className="flex flex-wrap items-center gap-4 mb-6 text-gray-600 dark:text-gray-400">
                            <div className="flex items-center space-x-2">
                              <User className="w-4 h-4" />
                              <span>@{profile?.username || t('publicProfile.unknownUsername')}</span>
                            </div>
                            <CredibilityChip
                              cred={
                                profile?.credibility?.overall_score != null
                                  ? {
                                      score: profile.credibility.overall_score,
                                      tier: profile.credibility.tier,
                                      accuracyPct: profile.credibility.prediction_accuracy_pct,
                                    }
                                  : null
                              }
                              size="md"
                            />
                            {profile?.location && (
                              <div className="flex items-center space-x-2">
                                <MapPin className="w-4 h-4" />
                                <span>{profile?.location}</span>
                              </div>
                            )}
                            {profile?.company && (
                              <div className="flex items-center space-x-2">
                                <Building className="w-4 h-4" />
                                <span>{profile?.company}</span>
                              </div>
                            )}
                          </div>

                          {/* Quick Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                            <Link
                              to={`/public/${profile?.username}/activity`}
                              className="text-center bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 transition"
                            >
                              <div className="text-2xl font-bold text-blue-400">{profile?.stats?.posts ?? 0}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.stats.posts')}</div>
                            </Link>

                            <Link
                              to={`/public/${profile?.username}/connections?tab=followers`}
                              className="text-center bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 transition"
                            >
                              <div className="text-2xl font-bold text-green-800 dark:text-green-400">{profile?.stats?.followers ?? 0}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.stats.followers')}</div>
                            </Link>

                            <Link
                              to={`/public/${profile?.username}/connections?tab=following`}
                              className="text-center bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 transition"
                            >
                              <div className="text-2xl font-bold text-purple-400">{profile?.stats?.following ?? 0}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.stats.following')}</div>
                            </Link>

                            <Link
                              to={`/public/${profile?.username}/watchlist`}
                              className="text-center bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 transition"
                            >
                              <div className="text-2xl font-bold text-orange-400">{profile?.watchlist?.count ?? 0}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.stats.watching')}</div>
                            </Link>

                            <Link
                              to={`/groups/${profile?.id}`}
                              className="text-center bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/50 hover:bg-cream-900/10 dark:hover:bg-gray-700/50 transition"
                            >
                              <div className="text-2xl font-bold text-pink-400">{profile?.stats?.groups_public_owned ?? 0}</div>
                              <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.stats.groupsOwned')}</div>
                            </Link>
                          </div>


                          {profile?.bio && (
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-6 bg-cream-100 dark:bg-gray-800/30 backdrop-blur-sm rounded-xl p-4 border border-cream-300 dark:border-gray-700/30">
                              {profile?.bio}
                            </p>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-3 flex-shrink-0">
                          {!isOwnProfile && (
                            <button
                              onClick={handleFollowToggle}
                              disabled={followLoading}
                              onAnimationEnd={() => setJustFollowed(false)}
                              className={`flex items-center justify-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 font-medium shadow-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                                isFollowing
                                  ? 'bg-gray-600 hover:bg-red-600 text-white'
                                  : 'bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white'
                              } ${justFollowed ? 'animate-pop' : ''}`}
                            >
                              {followLoading ? (
                                <Loader2 className="w-5 h-5 animate-spin" />
                              ) : isFollowing ? (
                                <UserMinus className="w-5 h-5" />
                              ) : (
                                <UserPlus className="w-5 h-5" />
                              )}
                              <span>{followLoading ? t('publicProfile.loading') : isFollowing ? t('publicProfile.unfollow') : t('publicProfile.follow')}</span>
                            </button>
                          )}

                          <div className="flex gap-2">
                            <button aria-label={t('publicProfile.messageAria')} className="p-3 bg-cream-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-cream-900/10 dark:hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm border border-cream-300 dark:border-gray-600/50">
                              <MessageCircle className="w-5 h-5" />
                            </button>
                            <button aria-label={t('publicProfile.shareProfileAria')} className="p-3 bg-cream-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-cream-900/10 dark:hover:bg-gray-600/50 transition-all duration-300 backdrop-blur-sm border border-cream-300 dark:border-gray-600/50">
                              <Share2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="max-w-6xl mx-auto px-6 mb-8">
                <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-xl border border-cream-300 dark:border-gray-700/50 overflow-hidden">
                  <div className="flex flex-wrap">
                    {[
                      { id: 'overview', label: t('publicProfile.tabs.overview'), icon: User },
                      { id: 'activity', label: t('publicProfile.tabs.activity'), icon: Activity },
                      { id: 'credibility', label: t('publicProfile.tabs.credibility'), icon: Shield },
                      { id: 'watchlist', label: t('publicProfile.tabs.watchlist'), icon: Eye },
                      { id: 'connections', label: t('publicProfile.tabs.connections'), icon: Users },
                    ].map((tab) => {
                      const Icon = tab.icon;
                      return (
                        <button
                          key={tab.id}
                          onClick={() => handleTabChange(tab.id)}
                          className={`flex items-center space-x-2 px-8 py-5 font-medium transition-all duration-300 relative ${
                            activeTab === tab.id
                              ? 'bg-gradient-to-r from-violet-500 to-indigo-600 text-white shadow-lg'
                              : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-cream-900/10 dark:hover:bg-gray-700/50'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span>{tab.label}</span>
                          {activeTab === tab.id && (
                            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 to-purple-400"></div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Tab Content */}
              <div className="max-w-6xl mx-auto px-6 mb-12">                {activeTab === 'overview' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Level & Progress */}
                    <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                        <Crown className="w-6 h-6 mr-3 text-yellow-700 dark:text-yellow-500" />
                        {t('publicProfile.levelProgress.title')}
                      </h3>
                      <div className="text-center mb-6">
                        <div className="text-4xl font-bold text-yellow-700 dark:text-yellow-500 mb-2">{t('publicProfile.levelProgress.levelLabel', { level: profile?.gamification?.level ?? 1 })}</div>
                        <div className="text-gray-600 dark:text-gray-400">{t('publicProfile.levelProgress.xp', { count: profile?.gamification?.total_points ?? 0 })}</div>
                        <div className="w-full bg-gray-300 dark:bg-gray-600/50 rounded-full h-3 mt-4">
                          <div
                            className="bg-gradient-to-r from-yellow-500 to-orange-500 h-full rounded-full transition-all duration-1000"
                            style={{ width: `${(profile?.gamification?.total_points ?? 0) % 100}%` }}
                          />
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          {t('publicProfile.levelProgress.xpToNext', { count: (profile?.gamification?.total_points ?? 0) % 100 })}
                        </div>
                      </div>
                      <div className="flex items-center justify-center space-x-6 text-center">
                        <div>
                          <div className="text-2xl font-bold text-purple-400">{profile?.gamification?.badges_count ?? 0}</div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">{t('publicProfile.levelProgress.badges')}</div>
                        </div>
                      </div>
                    </div>

                    {/* Sentiment */}
                    <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                        <BarChart3 className="w-6 h-6 mr-3 text-blue-500" />
                        {t('publicProfile.sentimentMix.title')}
                      </h3>
                      <div className="space-y-4">
                        {['bull', 'bear', 'neutral'].map((k) => {
                          const Icon = getSentimentIcon(k);
                          const color =
                            k === 'bull' ? 'bg-green-400'
                            : k === 'bear' ? 'bg-red-400'
                            : 'bg-gray-400';
                          const count = profile?.stats?.sentiment_mix?.[k] || 0;
                          const total = Math.max(profile?.stats?.posts || 1, 1);
                          const pct = Math.min(100, Math.max(0, (count / total) * 100));
                          const label =
                            k === 'bull' ? (
                              <span className="text-green-800 dark:text-green-400">{t('publicProfile.sentimentMix.bullish')}</span>
                            ) : k === 'bear' ? (
                              <span className="text-red-700 dark:text-red-400">{t('publicProfile.sentimentMix.bearish')}</span>
                            ) : (
                              <span className="text-gray-600 dark:text-gray-400">{t('publicProfile.sentimentMix.neutral')}</span>
                            );

                          return (
                            <div key={k} className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <Icon className={`w-5 h-5 ${k === 'bull' ? 'text-green-800 dark:text-green-400' : k === 'bear' ? 'text-red-700 dark:text-red-400' : 'text-gray-400'}`} />
                                {label}
                              </div>
                              <div className="flex items-center space-x-2">
                                <div className="w-24 bg-gray-300 dark:bg-gray-600/50 rounded-full h-2">
                                  <div className={`${color} h-full rounded-full`} style={{ width: `${pct}%` }} />
                                </div>
                                <span className="text-gray-900 dark:text-white text-sm w-8">{count}</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Top Tickers */}
                    <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-6">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
                        <Target className="w-6 h-6 mr-3 text-green-500" />
                        {t('publicProfile.topTickers.title')}
                      </h3>
                      <div className="space-y-3">
                        {(profile?.stats?.top_tickers || []).map((ticker, index) => {
                          // ✅ Ensure accuracy is always a number
                          const accuracy =
                            ticker.accuracy_pct != null
                              ? Number(ticker.accuracy_pct.toFixed?.(1))
                              : null;

                          let accuracyColor = "text-gray-600 dark:text-gray-400";
                          if (accuracy >= 70) accuracyColor = "text-green-800 dark:text-green-400";
                          else if (accuracy >= 40) accuracyColor = "text-yellow-400";
                          else if (accuracy > 0) accuracyColor = "text-red-700 dark:text-red-400";

                          return (
                            <div
                              key={ticker.symbol}
                              className="flex items-center justify-between p-3 bg-cream-100 dark:bg-gray-700/30 rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center text-sm font-bold text-blue-700 dark:text-blue-400">
                                  {index + 1}
                                </div>
                                <div>
                                  <Link
                                    to={`/stocks/${ticker.symbol}`}
                                    className="font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                  >
                                    {ticker.symbol}
                                  </Link>
                                  <div className="text-xs text-gray-600 dark:text-gray-400">
                                    {t('publicProfile.topTickers.statsLine', { posts: ticker.posts, total: ticker.total_predictions ?? 0, correct: ticker.correct_predictions ?? 0 })}{" "}
                                    {accuracy !== null ? (
                                      <span className={accuracyColor}>{t('publicProfile.topTickers.accuracySuffix', { pct: accuracy })}</span>
                                    ) : (
                                      t('publicProfile.topTickers.noPredictions')
                                    )}
                                  </div>
                                </div>
                              </div>
                              <ArrowRight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          );
                        })}
                      </div>
                    </div>



                  </div>
                )}

                {activeTab === 'activity' && profile?.id && (
                  <div className="space-y-6">
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">{t('publicProfile.postsTitle')}</h2>
                    {/* ✅ Redux-powered UserPosts component surfaces the posts */}
                    <UserPosts userId={profile?.id} />
                  </div>
                )}


                {activeTab === 'credibility' && (
                  <div className="space-y-6">
                    <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-8">
                      <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 flex items-center">
                        <Shield className="w-8 h-8 mr-4 text-blue-500" />
                        {t('publicProfile.credibility.title')}
                      </h2>

                      {/* Score + Ring */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                        <div className="text-center">
                          <div className="text-6xl font-bold text-blue-400 mb-4">
                            {profile?.credibility?.overall_score?.toFixed?.(1) || 'N/A'}
                          </div>
                          <div className="text-xl text-gray-600 dark:text-gray-300 mb-2">{t('publicProfile.credibility.overallScore')}</div>
                          <div className="text-lg text-blue-400 font-semibold">
                            {profile?.credibility?.tier || t('publicProfile.credibility.unranked')}
                          </div>
                        </div>

                        <div className="flex items-center justify-center">
                          <div className="relative w-48 h-48">
                            <div className="absolute inset-0 rounded-full border-8 border-gray-300 dark:border-gray-600/50"></div>
                            <div
                              className="absolute inset-0 rounded-full border-8 border-blue-500 border-t-transparent"
                              style={{
                                transform: `rotate(${(((profile?.credibility?.overall_score || 0) / 1000) * 360)}deg)`,
                                borderImage: 'conic-gradient(from 0deg, #3b82f6, #8b5cf6, #3b82f6) 1',
                              }}
                            />
                            <div className="absolute inset-0 flex items-center justify-center">
                              <div className="text-center">
                                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                                  {Math.round(((profile?.credibility?.overall_score || 0) / 1000) * 100)}%
                                </div>
                                <div className="text-sm text-gray-600 dark:text-gray-400">{t('publicProfile.credibility.progress')}</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Prediction Stats */}
                      <div className="mt-8">
                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{t('publicProfile.credibility.predictionStats')}</h3>
                        {Array.isArray(profile?.stats?.top_tickers) && profile?.stats.top_tickers.length > 0 ? (() => {
                          const totalPredictions = profile?.stats.top_tickers.reduce(
                            (acc, t) => acc + (t.total_predictions || 0), 0
                          );
                          const correctPredictions = profile?.stats.top_tickers.reduce(
                            (acc, t) => acc + (t.correct_predictions || 0), 0
                          );
                          const accuracyPct = totalPredictions > 0
                            ? ((correctPredictions / totalPredictions) * 100).toFixed(1)
                            : 0;

                          return (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-6 text-center">
                                <div className="text-2xl font-bold text-green-800 dark:text-green-400 mb-2">{accuracyPct}%</div>
                                <div className="text-gray-600 dark:text-gray-300">{t('publicProfile.credibility.accuracy')}</div>
                              </div>
                              <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-6 text-center">
                                <div className="text-2xl font-bold text-blue-400 mb-2">{totalPredictions}</div>
                                <div className="text-gray-600 dark:text-gray-300">{t('publicProfile.credibility.totalPredictions')}</div>
                              </div>
                              <div className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-6 text-center">
                                <div className="text-2xl font-bold text-purple-400 mb-2">{correctPredictions}</div>
                                <div className="text-gray-600 dark:text-gray-300">{t('publicProfile.credibility.correct')}</div>
                              </div>
                            </div>
                          );
                        })() : (
                          <div className="text-gray-600 dark:text-gray-400">{t('publicProfile.credibility.noPredictionsYet')}</div>
                        )}
                        <div className="mt-4 space-y-2">
                          <InvestmentDisclaimerNotice />
                          <AIProcessingNotice variant="profile" />
                        </div>
                      </div>

                      {/* Top Sectors */}
                      {Array.isArray(profile?.credibility?.top_sectors) && profile?.credibility.top_sectors.length > 0 && (
                        <div className="mt-10">
                          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">{t('publicProfile.credibility.topSectors')}</h3>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {profile?.credibility.top_sectors.map((sector, index) => (
                              <div key={index} className="bg-cream-100 dark:bg-gray-700/30 rounded-xl p-6 text-center">
                                <div className="text-2xl font-bold text-blue-400 mb-2">
                                  {typeof sector.score === 'number' ? sector.score.toFixed(1) : sector.score}
                                </div>
                                <div className="text-gray-600 dark:text-gray-300">{sector.name}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}


                {activeTab === 'watchlist' && (
                  <div className="space-y-6">
                    <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-8">
                      <div className="flex items-center justify-between mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                          <Eye className="w-8 h-8 mr-4 text-green-500" />
                          {t('publicProfile.watchlistTab.title')}
                        </h2>
                        <div className="text-green-800 dark:text-green-400 font-semibold text-lg">{t('publicProfile.watchlistTab.stocksCount', { count: profile?.watchlist?.count || 0 })}</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {(profile?.watchlist?.top || []).map((stock) => (
                          <Link
                            key={stock.symbol}
                            to={`/stocks/${stock.symbol}`}
                            className="block bg-cream-100 dark:bg-gray-700/30 rounded-xl p-6 hover:bg-cream-900/10 dark:hover:bg-gray-600/30
                                      transition-all duration-200 border border-cream-300 dark:border-gray-600/50
                                      hover:border-cream-900/20 dark:hover:border-gray-500/50"
                          >
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex-1">
                                <div className="text-xl font-bold text-gray-900 dark:text-white mb-1">{stock.symbol}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{stock.name}</div>
                              </div>
                              <div className="ml-4 p-2 bg-green-500/20 rounded-lg">
                                <TrendingUp className="w-5 h-5 text-green-800 dark:text-green-400" />
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-600 dark:text-gray-400">{t('publicProfile.watchlistTab.watching')}</span>
                              <ExternalLink className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                            </div>
                          </Link>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                      <div className="bg-gradient-to-br from-green-600/20 to-green-800/20 rounded-xl p-6 border border-green-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-green-800 dark:text-green-400">{t('publicProfile.watchlistTab.totalWatching')}</h3>
                          <Eye className="w-6 h-6 text-green-800 dark:text-green-400" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-white">{profile?.watchlist?.count || 0}</div>
                      </div>

                      <div className="bg-gradient-to-br from-blue-600/20 to-blue-800/20 rounded-xl p-6 border border-blue-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-blue-800 dark:text-blue-400">{t('publicProfile.watchlistTab.activeAlerts')}</h3>
                          <Target className="w-6 h-6 text-blue-800 dark:text-blue-400" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-white">{Math.floor((profile?.watchlist?.count || 0) * 0.3)}</div>
                      </div>

                      <div className="bg-gradient-to-br from-purple-600/20 to-purple-800/20 rounded-xl p-6 border border-purple-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-purple-800 dark:text-purple-400">{t('publicProfile.watchlistTab.avgPerformance')}</h3>
                          <BarChart3 className="w-6 h-6 text-purple-800 dark:text-purple-400" />
                        </div>
                        <div className="text-3xl font-bold text-gray-900 dark:text-white">
                          {avgPerformance !== null ? `${avgPerformance}%` : "N/A"}
                        </div>

                      </div>

                      <div className="bg-gradient-to-br from-orange-600/20 to-orange-800/20 rounded-xl p-6 border border-orange-500/30">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-400">{t('publicProfile.watchlistTab.topSector')}</h3>
                          <Briefcase className="w-6 h-6 text-orange-800 dark:text-orange-400" />
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-white">{topSector}</div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'connections' && (
                  <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-8">
                    {/* key ensures the tab remounts when switching to a different user */}
                    {profile?.id && (
                      <ConnectionsTab key={profile.id} userId={profile.id} isOwner={isOwnProfile} />
                    )}
                  </div>
                )}
              </div>

              {/* Footer Actions */}
              <div className="max-w-6xl mx-auto px-6 mb-12">
                <div className="bg-cream-100 dark:bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-cream-300 dark:border-gray-700/50 p-8">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                    <div className="text-center md:text-left">
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                        {t('publicProfile.footer.connectWith', { name: profile?.full_name || profile?.username })}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">{t('publicProfile.footer.subtitle')}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-4">
                      {!isOwnProfile && (
                        <button
                          onClick={handleFollowToggle}
                          disabled={followLoading}
                          onAnimationEnd={() => setJustFollowed(false)}
                          className={`flex items-center justify-center space-x-2 px-8 py-4 rounded-xl transition-all duration-300 font-medium shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${
                            isFollowing
                              ? 'bg-gray-600 hover:bg-red-600 text-white'
                              : 'bg-gradient-to-r from-violet-500 to-indigo-600 hover:from-violet-600 hover:to-indigo-700 text-white'
                          } ${justFollowed ? 'animate-pop' : ''}`}
                        >
                          {followLoading ? (
                            <Loader2 className="w-5 h-5 animate-spin" />
                          ) : isFollowing ? (
                            <UserMinus className="w-5 h-5" />
                          ) : (
                            <UserPlus className="w-5 h-5" />
                          )}
                          <span>{followLoading ? t('publicProfile.loading') : isFollowing ? t('publicProfile.unfollow') : t('publicProfile.follow')}</span>
                        </button>
                      )}
                      <button className="flex items-center justify-center space-x-2 px-8 py-4 bg-cream-100 dark:bg-gray-700/50 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-cream-900/10 dark:hover:bg-gray-600/50 transition-all duration-300 font-medium backdrop-blur-sm border border-cream-300 dark:border-gray-600/50">
                        <MessageCircle className="w-5 h-5" />
                        <span>{t('publicProfile.footer.message')}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Achievement Highlights */}
              <div className="max-w-6xl mx-auto px-6">
                <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/50 backdrop-blur-sm rounded-2xl border border-purple-500/30 p-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center">
                    <Sparkles className="w-7 h-7 mr-3 text-purple-400" />
                    {t('publicProfile.achievements.title')}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl p-6 border border-yellow-500/30">
                      <div className="flex items-center justify-between mb-4">
                        <Crown className="w-8 h-8 text-yellow-700 dark:text-yellow-400" />
                        <span className="text-xs bg-yellow-400/20 text-yellow-800 dark:text-yellow-300 px-2 py-1 rounded-full font-medium">{t('publicProfile.achievements.new')}</span>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('publicProfile.achievements.levelUp')}</h4>
                      <p className="text-yellow-700 dark:text-yellow-200 text-sm">{t('publicProfile.achievements.reachedLevel', { level: profile?.gamification?.level || 1 })}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-500/20 to-blue-500/20 rounded-xl p-6 border border-green-500/30">
                      <div className="flex items-center justify-between mb-4">
                        <Medal className="w-8 h-8 text-green-800 dark:text-green-400" />
                        <span className="text-xs bg-green-400/20 text-green-800 dark:text-green-300 px-2 py-1 rounded-full font-medium">{t('publicProfile.achievements.earned')}</span>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('publicProfile.achievements.activeAnalyst')}</h4>
                      <p className="text-green-800 dark:text-green-200 text-sm">{t('publicProfile.achievements.postsPublished', { count: profile?.stats?.posts || 0 })}</p>
                    </div>

                    <div className="bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl p-6 border border-blue-500/30">
                      <div className="flex items-center justify-between mb-4">
                        <Shield className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        <span className="text-xs bg-blue-400/20 text-blue-800 dark:text-blue-300 px-2 py-1 rounded-full font-medium">{t('publicProfile.achievements.tier')}</span>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('publicProfile.achievements.credibilityTier', { tier: profile?.credibility?.tier || t('publicProfile.credibility.unranked') })}</h4>
                      <p className="text-blue-700 dark:text-blue-200 text-sm">{t('publicProfile.achievements.scorePrefix', { score: profile?.credibility?.overall_score?.toFixed?.(1) || 'N/A' })}</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* end content */}
            </div>
  );
};

export default PublicProfilePage;